import React, { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/http";
import { Button } from "@/components/ui/button";
import { MessageSquare, Plus, Trash2, Send, Check, X } from "lucide-react";

const when = (iso) => { try { return new Date(iso).toLocaleDateString("en-AU", { day: "numeric", month: "short" }); } catch { return ""; } };

// Persisted text conversations with Azordi — list on the left, thread on the right. Memory lives in the database.
export const Conversations = () => {
  const [list, setList] = useState([]);
  const [conv, setConv] = useState(null);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState(null);
  const threadRef = useRef(null);

  const loadList = () => api.get("/assistant/conversations").then(({ data }) => setList(data)).catch(() => {});
  const open = (id) => api.get(`/assistant/conversations/${id}`).then(({ data }) => setConv(data)).catch(() => {});
  useEffect(() => { loadList(); }, []);
  // Scroll the thread box only — never the page (scrollIntoView moved the whole window).
  useEffect(() => { const el = threadRef.current; if (el) el.scrollTo({ top: el.scrollHeight, behavior: "smooth" }); }, [conv?.messages?.length, busy, pending]);

  const start = async () => { const { data } = await api.post("/assistant/conversations"); setConv(data); loadList(); };
  const remove = async (id) => { if (!window.confirm("Delete this conversation?")) return; await api.delete(`/assistant/conversations/${id}`); if (conv?.id === id) setConv(null); loadList(); };

  const send = async () => {
    const q = input.trim();
    if (!q || busy) return;
    let c = conv;
    if (!c) { const { data } = await api.post("/assistant/conversations"); c = data; setConv(data); }
    setInput(""); setPending(null); setBusy(true);
    setConv((x) => ({ ...x, messages: [...(x?.messages || []), { role: "user", text: q, at: new Date().toISOString() }] }));
    try {
      const { data } = await api.post(`/assistant/conversations/${c.id}/messages`, { message: q });
      const started = Date.now(); let result = null;
      while (Date.now() - started < 130000) {
        await new Promise((r) => setTimeout(r, 1500));
        try { const { data: s } = await api.get(`/assistant/chat/${data.job_id}`); if (s.status === "done" || s.status === "error") { result = s; break; } } catch (_) { /* keep polling */ }
      }
      if (result?.pending_action) setPending(result.pending_action);
      if (result?.status === "error") setConv((x) => ({ ...x, messages: [...x.messages, { role: "assistant", text: "⚠️ " + (result.error || "I couldn't reach the AI just now.") }] }));
      else if (!result) setConv((x) => ({ ...x, messages: [...x.messages, { role: "assistant", text: "⚠️ That took longer than expected — please try again." }] }));
      else await open(c.id);
      loadList();
    } catch (e) {
      setConv((x) => ({ ...x, messages: [...x.messages, { role: "assistant", text: "⚠️ " + (formatApiError(e.response?.data?.detail) || "I couldn't reach the AI just now.") }] }));
    } finally { setBusy(false); }
  };

  const confirmAction = async () => {
    const a = pending; setPending(null); setBusy(true);
    try { const { data } = await api.post("/assistant/execute", { type: a.type, args: a.args, origin: window.location.origin }); setConv((x) => ({ ...x, messages: [...x.messages, { role: "assistant", text: (data.ok ? "✅ " : "⚠️ ") + data.reply }] })); }
    catch (e) { setConv((x) => ({ ...x, messages: [...x.messages, { role: "assistant", text: "⚠️ " + (formatApiError(e.response?.data?.detail) || "That action failed.") }] })); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl border bg-card shadow-sm overflow-hidden grid md:grid-cols-[260px_1fr] min-h-[520px]" data-testid="intel-conversations">
      <aside className="border-b md:border-b-0 md:border-r bg-muted/30 flex flex-col">
        <div className="p-3 flex items-center justify-between border-b">
          <span className="font-mono-label text-[11px] uppercase tracking-wider text-muted-foreground flex items-center gap-1.5"><MessageSquare className="h-3.5 w-3.5" />Conversations</span>
          <Button size="sm" variant="outline" className="h-8 rounded-lg" onClick={start} data-testid="conv-new"><Plus className="h-3.5 w-3.5 mr-1" />New</Button>
        </div>
        <div className="flex-1 overflow-y-auto max-h-[200px] md:max-h-[520px] divide-y" data-testid="conv-list">
          {list.length === 0 && <p className="p-3 text-xs text-muted-foreground">No conversations yet — ask Azordi anything about your clinic.</p>}
          {list.map((c) => (
            <div key={c.id} className={`group flex items-start gap-2 px-3 py-2.5 cursor-pointer hover:bg-muted ${conv?.id === c.id ? "bg-primary/5 border-l-2 border-primary" : ""}`} onClick={() => open(c.id)} data-testid={`conv-item-${c.id}`}>
              <div className="flex-1 min-w-0"><div className="text-sm font-medium truncate">{c.title}</div><div className="text-[11px] text-muted-foreground truncate">{when(c.updated_at)} · {c.count} msgs{c.preview ? ` · ${c.preview}` : ""}</div></div>
              <button onClick={(e) => { e.stopPropagation(); remove(c.id); }} className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive" data-testid={`conv-delete-${c.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          ))}
        </div>
      </aside>
      <section className="flex flex-col">
        <div ref={threadRef} className="overflow-y-auto p-4 space-y-3 h-[440px]" data-testid="conv-thread">
          {!conv && <div className="h-full flex items-center justify-center text-sm text-muted-foreground text-center px-6">Pick a conversation or start a new one. Azordi remembers what you've discussed before and knows today's numbers.</div>}
          {conv?.messages?.length === 0 && <p className="text-sm text-muted-foreground">What would you like to look at? Try “How did last week compare to the week before?” or “Who should I text today?”</p>}
          {conv?.messages?.map((m, i) => (
            <div key={i} className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${m.role === "user" ? "ml-auto bg-primary text-primary-foreground" : "bg-muted"}`} data-testid={`conv-msg-${i}`}>{m.text}</div>
          ))}
          {busy && <div className="bg-muted rounded-2xl px-3.5 py-2.5 text-sm text-muted-foreground w-fit animate-pulse" data-testid="conv-thinking">Azordi is thinking…</div>}
          {pending && (
            <div className="rounded-xl border border-primary/40 bg-primary/5 p-3 text-sm" data-testid="conv-pending">
              <div className="font-medium mb-2">{pending.summary || "Do this now?"}</div>
              <div className="flex gap-2"><Button size="sm" className="rounded-lg h-8" onClick={confirmAction} data-testid="conv-confirm"><Check className="h-3.5 w-3.5 mr-1" />Yes, do it</Button><Button size="sm" variant="outline" className="rounded-lg h-8" onClick={() => setPending(null)} data-testid="conv-cancel"><X className="h-3.5 w-3.5 mr-1" />Cancel</Button></div>
            </div>
          )}
        </div>
        <div className="border-t p-3 flex gap-2">
          <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} placeholder="Ask Azordi…" className="flex-1 h-11 rounded-lg border border-input bg-background px-3 text-sm" data-testid="conv-input" />
          <Button onClick={send} disabled={busy || !input.trim()} className="h-11 rounded-lg" data-testid="conv-send"><Send className="h-4 w-4" /></Button>
        </div>
      </section>
    </div>
  );
};
